import { useCallback, useEffect, useRef } from 'react';

type UseBoundedQueryRefreshProps<TData> = {
  refetch: () => Promise<{ data: TData | undefined }>;
  followUpDelayMs?: number;
};

type RefreshQueryOptions<TData> = {
  shouldScheduleFollowUp?: (data: TData | undefined) => boolean;
};

export function useBoundedQueryRefresh<TData>({
  refetch,
  followUpDelayMs = 1000,
}: UseBoundedQueryRefreshProps<TData>) {
  const followUpTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearScheduledRefresh = useCallback(() => {
    if (!followUpTimeoutRef.current) {
      return;
    }

    clearTimeout(followUpTimeoutRef.current);
    followUpTimeoutRef.current = null;
  }, []);

  useEffect(() => {
    return clearScheduledRefresh;
  }, [clearScheduledRefresh]);

  return useCallback(
    async ({ shouldScheduleFollowUp }: RefreshQueryOptions<TData> = {}) => {
      clearScheduledRefresh();

      const result = await refetch();

      if (!shouldScheduleFollowUp?.(result.data)) {
        return;
      }

      followUpTimeoutRef.current = setTimeout(() => {
        void refetch();
        followUpTimeoutRef.current = null;
      }, followUpDelayMs);
    },
    [clearScheduledRefresh, followUpDelayMs, refetch],
  );
}
